import React, { useMemo, useState } from "react";
import { Grid, Table, Modal, Descriptions, Button, Dropdown, Checkbox } from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import SalesRecordActions from "./SalesRecordActions";
import { accessChecker, formatToCompanyTimezone, numberFormatTax } from "../../../lib/functions";
import { useNavigate } from "react-router-dom";
import { selectAuthInfo } from "../../Auth/authSelectors";
import Swal from "sweetalert2";

import { useDispatch } from "react-redux";

import { fetchSalesRecord, retryTatmeenSales } from "../SalesRecordThunk";
import { setVisibleColumns, resetVisibleColumns } from "../SalesRecordSlice";

import { RedoOutlined, SettingOutlined } from "@ant-design/icons";

// "No.", the Voucher No identifier, and Actions are always shown -- the
// Columns toggle below only governs the rest.
const LOCKED_SUMMARY_COLUMNS = ["no", "sale_voucher_no", "actions"];

const SalesRecordTable = ({ printMode = false }) => {
  const navigate = useNavigate();
  const authInfo = useSelector(selectAuthInfo);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const { useBreakpoint } = Grid;

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const isGstEnabled = authInfo?.gst_enabled === "yes";
  const showGstColumns = isIndia && isGstEnabled;
  const showTax = !isIndia || isGstEnabled;
  const dispatch = useDispatch();

  const showTatmeenColumn =
    authInfo?.is_tatmeen_enabled === "yes" &&
    authInfo?.country === "United Arab Emirates" && (authInfo?.subscription == 'PharmaERP_Pro' || authInfo?.subscription == 'PharmaERP_Standard' || authInfo?.subscription == 'PharmaERP_Basic');
  const hasTatmeenEntryAccess = accessChecker("tatmeen_entry");
  const [tatmeenModal, setTatmeenModal] = useState({
    open: false,
    record: null
  });

  const taxLabel = isIndia ? "GST" : "VAT";

  const { list, loading, filters, details, visibleColumns } = useSelector(
    (state) => state.salesRecord,
  );
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);

  const money = (v) => Number(v || 0).toFixed(2);
  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleRetryTatmeen = async () => {

    const result = await Swal.fire({
      title: "Retry Tatmeen Sync?",
      text: `Sales ${tatmeenModal.record.sale_voucher_no} will be reprocessed.`,
      icon: "question",
      showCancelButton: true,
    });

    if (!result.isConfirmed) return;

    try {

      await dispatch(
        retryTatmeenSales(
          tatmeenModal.record.sale_id
        )
      ).unwrap();

      Swal.fire(
        "Success",
        "Tatmeen Action has been queued successfully.",
        "success"
      );

      setTatmeenModal({
        open: false,
        record: null
      });

      dispatch(fetchSalesRecord({
        filters
      }));

    } catch (err) {
      const message =
        typeof err === "string" ? err :
          err?.message ? err.message :
            err?.response?.data?.message ? err.response.data.message :
              "Retry failed";

      Swal.fire("Failed", message, "error");
    }

  };

  // The full set of possible summary columns, each tagged with a stable
  // `key` so the Columns toggle (and sessionStorage-persisted
  // visibleColumns) can select among them regardless of dataIndex/render
  // shape. Conditional entries (showTax/showGstColumns/tatmeen) still gate
  // whether a column is even offered as an option -- the toggle only
  // governs which of the APPLICABLE columns are currently shown.
  const allSummaryColumns = useMemo(
    () => [
      { key: "no", title: "No.", render: (_, __, i) => i + 1, width: 60, fixed: isMobile ? false : "left" },
      { key: "sale_voucher_no", title: "Voucher No", dataIndex: "sale_voucher_no", fixed: isMobile ? false : "left" },
      {
        key: "date",
        title: "Date",
        dataIndex: "created_date",
        render: (v) => v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
      },
      { key: "acc_name", title: "Customer Name", dataIndex: "acc_name" },
      { key: "employee_name", title: "Employee Name", dataIndex: "employee_name" },
      { key: "full_name", title: "Created By", dataIndex: "full_name" },
      { key: "mode_of_pay", title: "Mode Of Pay", dataIndex: "mode_of_pay" },
      {
        key: "sub_total",
        title: "Sub Total",
        width: 125,
        render: (_, record) =>
          money(
            Number(record.sub_total || 0) +
            Number(record.total_discount || 0) -
            Number(record.total_tax || 0)
          ),
        align: "right",
      },
      {
        key: "other_service_cost",
        title: "Service Cost",
        width: 110,
        dataIndex: "other_service_cost",
        render: money,
        align: "right",
      },
      { key: "total_discount", title: "Discount", dataIndex: "total_discount", render: money, width: 100, align: "right", },
      showTax && { key: "total_tax", title: taxLabel, dataIndex: "total_tax", render: money, width: 125, align: "right" },
      showGstColumns && {
        key: "cgst_amount",
        title: "CGST",
        dataIndex: "cgst_amount",
        render: numberFormatTax,
        align: "right",
      },

      showGstColumns && {
        key: "sgst_amount",
        title: "SGST",
        dataIndex: "sgst_amount",
        render: numberFormatTax,
        align: "right",
      },

      showGstColumns && {
        key: "igst_amount",
        title: "IGST",
        dataIndex: "igst_amount",
        render: numberFormatTax,
        align: "right",
      },
      { key: "total_amount", title: "Total Amount", dataIndex: "total_amount", render: money, width: 120, align: "right" },
      { key: "paid_total", title: "Paid", dataIndex: "paid_total", render: money, width: 120, align: "right" },
      { key: "due_total", title: "Due", dataIndex: "due_total", render: money, width: 120, align: "right" },
      hasTatmeenEntryAccess && showTatmeenColumn && {
        key: "tatmeen_sent_status",
        title: "Tatmeen Status",
        dataIndex: "tatmeen_sent_status",
        render: (status) => {
          const colorMap = {
            sent: "#52c41a",
            pending: "#faad14",
            failed: "#ff4d4f",
            cancelled: "#8c8c8c",
            not_started: "#8c8c8c",
          };

          return (
            <span
              style={{
                color: colorMap[status] || "#000",
                fontWeight: 500,
              }}
            >
              {status || "-"}
            </span>
          );
        },
      },
      !printMode && {
        key: "actions",
        title: "Actions",
        align: "center",
        fixed: isMobile ? false : "right",
        width: 110,
        render: (_, record) => <SalesRecordActions record={record} setTatmeenModal={setTatmeenModal} hasTatmeenEntryAccess={hasTatmeenEntryAccess} />
      },
    ].filter(Boolean),
    [printMode, isMobile, showTax, showGstColumns, taxLabel, hasTatmeenEntryAccess, showTatmeenColumn]
  );

  const summaryColumnOptions = allSummaryColumns
    .filter((col) => !LOCKED_SUMMARY_COLUMNS.includes(col.key))
    .map((col) => ({ label: col.title, value: col.key }));

  const summaryColumns = useMemo(
    () =>
      printMode
        ? allSummaryColumns
        : allSummaryColumns.filter(
            (col) => LOCKED_SUMMARY_COLUMNS.includes(col.key) || visibleColumns.includes(col.key)
          ),
    [allSummaryColumns, visibleColumns, printMode]
  );

  const withItemColumns = useMemo(
    () => [
      { title: "No.", render: (_, __, i) => i + 1, width: 60 },
      { title: "Voucher No", dataIndex: "sale_voucher_no" },
      {
        title: "Date",
        dataIndex: "created_date",
        render: (v) =>
          v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
      },
      { title: "Customer Name", dataIndex: "acc_name" },
      { title: "Employee Name", dataIndex: "employee_name" },
      { title: "Item Name", dataIndex: "item_name" },
      { title: "Item Code", dataIndex: "display_text" },
      { title: "Rate", dataIndex: "item_rate", render: money, align: "right" },
      { title: "Qty", dataIndex: "item_qty", align: "right" },
      { title: "Discount%", dataIndex: "item_discount_per", align: "right" },
      showTax && { title: "Tax%", dataIndex: "item_tax_per", align: "right" },
      { title: "Total", dataIndex: "item_total", render: money, align: "right" },
      !printMode && {
        title: "Actions",
        align: "center",
        fixed: isMobile ? false : "right",
        width: 120,

        render: (_, record) => <SalesRecordActions record={record} />
      },
    ].filter(Boolean),
    [printMode]
  );

  const byItemColumns = useMemo(
    () => [
      { title: "No.", render: (_, __, i) => i + 1 },
      { title: "Voucher No", dataIndex: "sale_voucher_no" },
      {
        title: "Date",
        dataIndex: "created_date",
        render: (v) =>
          v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
      },
      { title: "Customer Name", dataIndex: "acc_name" },
      { title: "Item Name", dataIndex: "item_name" },
      { title: "Item Code", dataIndex: "display_text" },
      { title: "Qty", dataIndex: "item_qty", align: "right" },
      { title: "Rate", dataIndex: "item_rate", render: money, align: "right" },
      { title: "Discount%", dataIndex: "item_discount_per", align: "right" },
      { title: "Tax%", dataIndex: "item_tax_per", align: "right" },
      {
        title: "Total",
        dataIndex: "item_total",
        render: money,
        align: "right",
      },
    ],
    [],
  );

  const byTagColumns = useMemo(
    () => [
      { title: "No.", render: (_, __, i) => i + 1 },
      { title: "Voucher No", dataIndex: "sale_voucher_no" },
      {
        title: "Date",
        dataIndex: "created_date",
        render: (v) => (v ? dayjs(v).format("DD-MMM-YYYY") : "-"),
      },
      { title: "Customer Name", dataIndex: "acc_name" },
      { title: "Tag Group", dataIndex: "group_name" },
      { title: "Tag Field", dataIndex: "field_name" },
      { title: "Item Name", dataIndex: "item_name" },
      { title: "Item Code", dataIndex: "display_text" },
      { title: "Qty", dataIndex: "item_qty", align: "right" },
      { title: "Rate", dataIndex: "item_rate", render: money, align: "right" },
      { title: "Discount%", dataIndex: "item_discount_per", align: "right" },
      { title: "Tax%", dataIndex: "item_tax_per", align: "right" },
      {
        title: "Total",
        dataIndex: "item_total",
        render: money,
        align: "right",
      },
    ],
    [],
  );
  const byGroupColumns = useMemo(
    () => [
      { title: "No.", render: (_, __, i) => i + 1 },
      { title: "Voucher No", dataIndex: "sale_voucher_no" },
      {
        title: "Date",
        dataIndex: "created_date",
        render: (v) =>
          v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
      },
      { title: "Customer Name", dataIndex: "acc_name" },
      { title: "Group Name", dataIndex: "group_name" },
      { title: "Item Name", dataIndex: "item_name" },
      { title: "Item Code", dataIndex: "display_text" },
      { title: "Qty", dataIndex: "item_qty", align: "right" },
      { title: "Rate", dataIndex: "item_rate", render: money, align: "right" },
      { title: "Discount%", dataIndex: "item_discount_per", align: "right" },
      { title: "Tax%", dataIndex: "item_tax_per", align: "right" },
      {
        title: "Total",
        dataIndex: "item_total",
        render: money,
        align: "right",
      },
    ],
    [],
  );

  const byCategoryColumns = useMemo(
    () => [
      { title: "No.", render: (_, __, i) => i + 1 },
      { title: "Voucher No", dataIndex: "sale_voucher_no" },
      {
        title: "Date",
        dataIndex: "created_date",
        render: (v) =>
          v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
      },
      { title: "Customer Name", dataIndex: "acc_name" },
      { title: "Category Name", dataIndex: "category_name" },
      { title: "Item Name", dataIndex: "item_name" },
      { title: "Item Code", dataIndex: "display_text" },
      { title: "Qty", dataIndex: "item_qty", align: "right" },
      { title: "Rate", dataIndex: "item_rate", render: money, align: "right" },
      { title: "Discount%", dataIndex: "item_discount_per", align: "right" },
      { title: "Tax%", dataIndex: "item_tax_per", align: "right" },
      {
        title: "Total",
        dataIndex: "item_total",
        render: money,
        align: "right",
      },
    ],
    [],
  );

  const columns = useMemo(() => {
    switch (filters.filterType) {
      case "By Group":
        return byGroupColumns;
      case "By Category":
        return byCategoryColumns;
      case "By Item":
      case "By Tag":
        return byTagColumns;
      default:
        if (filters.recordType === "with item") return withItemColumns;
        return summaryColumns;
    }
  }, [
    filters,
    withItemColumns,
    byItemColumns,
    byGroupColumns,
    byCategoryColumns,
    summaryColumns,
    byTagColumns,
  ]);

  const dataSource = useMemo(() => {
    if (
      filters.recordType === "with item" &&
      !["By Item", "By Tag", "By Group", "By Category"].includes(
        filters.filterType,
      )
    ) {
      return details.flatMap((master) =>
        (master.details || []).map((detail, index) => {
          return {
            ...detail,
            key: `${master.sale_id}-${index}`,
            sale_voucher_no: master.sale_voucher_no,
            acc_name: master.acc_name,
            employee_name: master.employee_name,
            created_date: master.created_date,
            qty_display: `${detail.item_qty || 0} ${detail.unit_symbol || ""}${detail.conversion > 1 ? `, ${detail.retail_qty || 0} ${detail.base_unit_name || ""}` : ""}`,
            // ✅ FIX: Use the same format as purchase record
            unitDetails: `Total : Master U. Qty = ${detail.item_qty || 0}, Base U. Qty = ${detail.retail_qty || 0}`,
          };
        }),
      );
    }

    // For item-based filters
    if (
      ["By Group", "By Category", "By Tag", "By Item"].includes(
        filters.filterType,
      )
    ) {
      return details.map((item, index) => {
        return {
          ...item,
          key: index,
          qty_display: `${item.item_qty || 0} ${item.unit_symbol || ""}${item.conversion > 1 ? `, ${item.retail_qty || 0} ${item.base_unit_name || ""}` : ""}`,
          // ✅ FIX: Use the same format as purchase record
          unitDetails: `Total : Master U. Qty = ${item.item_qty || 0}, Base U. Qty = ${item.retail_qty || 0}`,
        };
      });
    }

    return list.map((item, index) => ({
      ...item,
      key: index,
    }));
  }, [filters, list, details]);

  const expandable =
    filters.recordType === "with item"
      ? {
        expandedRowRender: (record) => (
          <div style={{ fontSize: "12px", color: "#555" }}>
            {record.unitDetails}
          </div>
        ),
        rowExpandable: (record) => !!record.unitDetails,
      }
      : undefined;

  const getSummary = () => {
    if (dataSource.length === 0) return null;

    // Case 1: "with item" record type
    if (
      filters.recordType === "with item" &&
      !["By Item", "By Tag", "By Group", "By Category"].includes(
        filters.filterType,
      )
    ) {
      const totals = dataSource.reduce(
        (acc, row) => {
          acc.master_qty += Number(row.item_qty || 0);
          acc.base_qty += Number(row.retail_qty || 0);
          acc.total += Number(row.item_total || 0);
          return acc;
        },
        { master_qty: 0, base_qty: 0, total: 0 },
      );

      return (
        <Table.Summary fixed="bottom">
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={3} align="left">
              <strong>Grand Total :</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} colSpan={3} align="left">
              <strong>
                Master U. Qty = {money(totals.master_qty)}, Base U. Qty ={" "}
                {money(totals.base_qty)}
              </strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3} colSpan={7} align="right">
              <strong>{money(totals.total)}</strong>
            </Table.Summary.Cell>
            {!printMode && (
              <Table.Summary.Cell index={3} align="center"></Table.Summary.Cell>
            )}
          </Table.Summary.Row>
        </Table.Summary>
      );
    }

    // Case 2: Item-based filters (By Item, By Group, By Category, By Tag)
    if (
      ["By Item", "By Tag", "By Group", "By Category"].includes(
        filters.filterType,
      )
    ) {
      const totals = dataSource.reduce(
        (acc, row) => {
          acc.master_qty += Number(row.item_qty || 0);
          acc.base_qty += Number(row.retail_qty || 0);
          acc.total += Number(row.item_total || 0);
          return acc;
        },
        { master_qty: 0, base_qty: 0, total: 0 },
      );

      return (
        <Table.Summary fixed>
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={1} align="right">
              <strong>Grand Total :</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} colSpan={3} align="left">
              <strong>
                Master U. Qty = {money(totals.master_qty)}, Base U. Qty ={" "}
                {money(totals.base_qty)}
              </strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} colSpan={12} align="right">
              <strong>{money(totals.total)}</strong>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </Table.Summary>
      );
    }

    if (filters.recordType === "without item") {
      const taxColOffset = (showTax ? 1 : 0) + (showGstColumns ? 3 : 0);
      const totals = dataSource.reduce(
        (acc, row) => {
          acc.sub_total += Number(row.sub_total || 0) - Number(row.total_tax || 0);
          acc.other_service_cost += Number(row.other_service_cost || 0);
          acc.total_discount += Number(row.total_discount || 0);
          acc.total_tax += Number(row.total_tax || 0);
          acc.cgst += Number(row.cgst_amount || 0);
          acc.sgst += Number(row.sgst_amount || 0);
          acc.igst += Number(row.igst_amount || 0);
          acc.total_amount += Number(row.total_amount || 0);
          acc.paid_total += Number(row.paid_total || 0);
          acc.due_total += Number(row.due_total || 0);
          return acc;
        },
        {
          sub_total: 0,
          other_service_cost: 0,
          total_discount: 0,
          total_tax: 0,
          cgst: 0,
          sgst: 0,
          igst: 0,
          total_amount: 0,
          paid_total: 0,
          due_total: 0,
        },
      );

      return (
        <Table.Summary fixed>
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={4} align="right">
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right">
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="right">
              <strong>Grand Total :</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={4} align="right">
              <strong>{money(totals.sub_total)}</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={5} align="right">
              <strong>{money(totals.other_service_cost)}</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={6} align="right">
              <strong>{money(totals.total_discount)}</strong>
            </Table.Summary.Cell>
            {showTax && (
              <Table.Summary.Cell index={7} align="right">
                <strong>{money(totals.total_tax)}</strong>
              </Table.Summary.Cell>
            )}

            {showGstColumns && (
              <Table.Summary.Cell index={8} align="right">
                <strong>{numberFormatTax(totals.cgst || 0)}</strong>
              </Table.Summary.Cell>
            )}
            {showGstColumns && (
              <Table.Summary.Cell index={9} align="right">
                <strong>{numberFormatTax(totals.sgst || 0)}</strong>
              </Table.Summary.Cell>
            )}
            {showGstColumns && (
              <Table.Summary.Cell index={10} align="right">
                <strong>{numberFormatTax(totals.igst || 0)}</strong>
              </Table.Summary.Cell>
            )}

            <Table.Summary.Cell index={7 + taxColOffset} align="right">
              <strong>{money(totals.total_amount)}</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={8 + taxColOffset} align="right">
              <strong>{money(totals.paid_total)}</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={9 + taxColOffset} align="right">
              <strong>{money(totals.due_total)}</strong>
            </Table.Summary.Cell>
            {!printMode && (
              <Table.Summary.Cell index={10 + taxColOffset} align="center"></Table.Summary.Cell>
            )}
          </Table.Summary.Row>
        </Table.Summary>
      );
    }

    return null;
  };

  if (printMode) {
    return (
      <table className="ledger-print-table">
        <colgroup>
          <col style={{ width: "4%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "32%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "12%" }} />
        </colgroup>

        <tbody>
          <tr className="sales-return-header">
            <td>No.</td>
            <td>Date</td>
            <td>Particular</td>
            <td>VCH No</td>
            <td>VCH Type</td>
            <td style={{ textAlign: "right" }}>Debit</td>
            <td style={{ textAlign: "right" }}>Credit</td>
            <td style={{ textAlign: "right" }}>Balance</td>
          </tr>

          {dataSource.map((row, index) => (
            <tr key={row.key || index}>
              <td>{index + 1}</td>

              <td style={{
                maxWidth: "150px",
                wordBreak: "break-word",
                whiteSpace: "normal",
              }}>{row.sale_voucher_no}</td>

              <td>
                {row.created_date
                  ? formatToCompanyTimezone(row.created_date).split(" ")[0]
                  : "-"}
              </td>

              <td>{row.acc_name}</td>

              <td style={{ textAlign: "right" }}>
                {money(row.sub_total)}
              </td>

              <td style={{ textAlign: "right" }}>
                {money(row.total_discount)}
              </td>

              <td style={{ textAlign: "right" }}>
                {money(row.total_tax)}
              </td>

              <td style={{ textAlign: "right" }}>
                {money(row.total_amount)}
              </td>

            </tr>

          ))}
        </tbody>
        <tr className="ledger-total-row">
          <td colSpan={4} style={{ textAlign: "right" }}>
            Grand Total
          </td>

          <td style={{ textAlign: "right" }}>
            {money(
              _.sumBy(dataSource, (r) =>
                Number(r.sub_total || 0)
              )
            )}
          </td>

          <td style={{ textAlign: "right" }}>
            {money(
              _.sumBy(dataSource, (r) =>
                Number(r.total_discount || 0)
              )
            )}
          </td>

          <td style={{ textAlign: "right" }}>
            {money(
              _.sumBy(dataSource, (r) =>
                Number(r.total_tax || 0)
              )
            )}
          </td>

          <td style={{ textAlign: "right" }}>
            {money(
              _.sumBy(dataSource, (r) =>
                Number(r.total_amount || 0)
              )
            )}
          </td>
        </tr>
      </table>
    );
  }
  // The Columns toggle only applies to the summary ("without item") view --
  // the item-breakdown views already have few enough columns.
  const isSummaryView =
    !["By Group", "By Category", "By Item", "By Tag"].includes(filters.filterType) &&
    filters.recordType !== "with item";

  return (
    <>
      {isSummaryView && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <Dropdown
            open={columnMenuOpen}
            onOpenChange={setColumnMenuOpen}
            trigger={["click"]}
            dropdownRender={() => (
              <div
                style={{
                  width: 270,
                  background: "#fff",
                  borderRadius: 10,
                  overflow: "hidden",
                  boxShadow: "0 6px 20px rgba(0,0,0,.12)",
                }}
              >
                <div
                  style={{
                    padding: "14px 16px",
                    borderBottom: "1px solid #f0f0f0",
                    fontWeight: 600,
                    fontSize: 15,
                  }}
                >
                  Show / Hide Columns
                </div>

                <div
                  style={{
                    maxHeight: 320,
                    overflowY: "auto",
                    padding: "12px 16px",
                  }}
                >
                  <Checkbox.Group
                    value={visibleColumns}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      gap: 2,
                    }}
                    onChange={(checkedValues) => {
                      const next = Array.from(
                        new Set([...checkedValues, ...LOCKED_SUMMARY_COLUMNS])
                      );
                      dispatch(setVisibleColumns(next));
                    }}
                  >
                    {summaryColumnOptions.map((col) => (
                      <Checkbox
                        key={col.value}
                        value={col.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          margin: 0,
                          padding: "6px 8px",
                          borderRadius: 6,
                          fontSize: 14,
                        }}
                      >
                        {col.label}
                      </Checkbox>
                    ))}
                  </Checkbox.Group>
                </div>

                <div
                  style={{
                    borderTop: "1px solid #f0f0f0",
                    padding: 12,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                  }}
                >
                  <Button onClick={() => dispatch(resetVisibleColumns())}>
                    Reset
                  </Button>
                  <Button type="primary" onClick={() => setColumnMenuOpen(false)}>
                    Done
                  </Button>
                </div>
              </div>
            )}
          >
            <Button icon={<SettingOutlined />}>Columns</Button>
          </Dropdown>
        </div>
      )}

      <Table
        rowKey="key"
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={handleTableChange}
        bordered
        scroll={printMode ? undefined : { x: columns.reduce((sum, col) => sum + (col.width || 150), 0), y: "65vh" }}
        expandable={expandable}
        tableLayout="fixed"
        // sticky
        data-testid="sales-record-table"
        summary={getSummary}
        onRow={(record) => ({
          style: { cursor: 'pointer' },
          onClick: (e) => {
            if (
              e.target.closest(".ant-btn") ||
              e.target.closest(".ant-popover")
            ) {
              return;
            }
            const saleId = record?.sale_id || record?.id;
            if (!saleId) return;
            if (
              authInfo?.branch_company_type === "Pharmacy" &&
              record?.insurance_id
            ) {
              navigate(`/sales/quick-sales-pharma-voucher/${saleId}`);
            } else {
              navigate(`/sales/quick-sales-voucher/${saleId}`);
            }
          },
        })}

      />

      <Modal
        title="Tatmeen Details"
        open={tatmeenModal.open}
        onCancel={() =>
          setTatmeenModal({
            open: false,
            record: null,
          })
        }
        footer={
          hasTatmeenEntryAccess
            ? [
              <Button
                key="retry"
                type="primary"
                icon={<RedoOutlined />}
                onClick={handleRetryTatmeen}
              >
                Mark Sales Entry
              </Button>,
            ]
            : null
        }
      >
        <Descriptions bordered column={1}>
          <Descriptions.Item label="SSCC">
            {tatmeenModal.record?.sscc_code || "-"}
          </Descriptions.Item>

          <Descriptions.Item label="Tatmeen Status">
            {tatmeenModal.record?.tatmeen_sent_status || "-"}
          </Descriptions.Item>

          <Descriptions.Item label="SSCC Status">
            {tatmeenModal.record?.tatmeen_sscc_status || "-"}
          </Descriptions.Item>

          <Descriptions.Item label="Message ID">
            {tatmeenModal.record?.tatmeen_message_id || "-"}
          </Descriptions.Item>

          <Descriptions.Item label="Retry Count">
            {tatmeenModal.record?.tatmeen_retry_count || 0}
          </Descriptions.Item>

          <Descriptions.Item label="Failure Reason">
            {tatmeenModal.record?.tatmeen_failure_reason ? (
              <div
                style={{
                  maxHeight: 200,
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "monospace",
                  fontSize: 11,
                  background: "#fafafa",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #f0f0f0",
                }}
              >
                {tatmeenModal.record.tatmeen_failure_reason}
              </div>
            ) : (
              "-"
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Manual Action">
            {tatmeenModal.record?.tatmeen_manual_action_required || "-"}
          </Descriptions.Item>

          {/* <Descriptions.Item label="Last Retry">
            {tatmeenModal.record?.tatmeen_last_retry_at || "-"}
          </Descriptions.Item> */}
        </Descriptions>
      </Modal>
    </>

  );
};

export default SalesRecordTable;
